import { useCallback, useEffect, useRef, useState } from "react";
import { CUP_GRINDER_HISTORY_LIMIT } from "../../Components/Device/Tabs/maintenance/cupGrinderSensorHistory";

// Sozlesme: BayinerIOT-Workspace- docs/api-contracts/cup-grinder-sensor-live.md (#132)
// Tablet yalniz panel "Sensör izlemeyi başlat" dediginde (Operasyon Modu
// aciksa) saniyede 1 yayin yapar; bu surede mesaj gelmezse veri bayat sayilir.
export const STALE_MS = 5000;

/**
 * Fincan + degirmen sensoru canli izleme (askı hook'unun esi; goz secimi
 * yok, tum gozler birlikte). `ready` olunca `cupGrinderSensor:watch`,
 * ayrilirken `cupGrinderSensor:unwatch`.
 *
 * @returns {{ status: "connecting"|"watching"|"error", error: string|null,
 *   live: object|null, receivedAt: number|null, closedReason: string|null,
 *   running: boolean, remoteError: string|null,
 *   history: { receivedAt: number, data: object }[],
 *   start: Function, stop: Function }}
 */
export default function useCupGrinderSensorLive(socket, ready, serialNo) {
  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState(null);
  const [live, setLive] = useState(null);
  const [receivedAt, setReceivedAt] = useState(null);
  const [closedReason, setClosedReason] = useState(null);
  // Son okumalar (eskiden yeniye) — CSV olarak indirilir. Yayın durunca
  // silinmez; yalnız cihaz değişince sıfırlanır.
  const [history, setHistory] = useState([]);
  const [, forceTick] = useState(0);
  // Panel uzaktan oturumu istedi mi (yeniden bağlanmada tekrar başlatılır).
  const [running, setRunning] = useState(false);
  const [remoteError, setRemoteError] = useState(null);
  const runningRef = useRef(false);
  runningRef.current = running;

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    const onUpdate = (msg) => {
      if (!msg || msg.serialNo !== serialNo || !msg.data) return;
      if (msg.data.active === false) {
        setLive(null);
        setReceivedAt(null);
        setClosedReason(msg.data.reason || "closed");
        return;
      }
      if (msg.data.v !== undefined && msg.data.v !== 1) return;
      const at = Date.now();
      setLive(msg.data);
      setReceivedAt(at);
      setHistory((prev) =>
        [...prev, { receivedAt: at, data: msg.data }].slice(
          -CUP_GRINDER_HISTORY_LIMIT,
        ),
      );
      setClosedReason(null);
    };
    socket.on("cupGrinderSensor:update", onUpdate);
    return () => socket.off("cupGrinderSensor:update", onUpdate);
  }, [socket, serialNo]);

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    if (!ready) {
      setStatus("connecting");
      return undefined;
    }
    // Yeniden baglanmada ready yeniden true olur — izleme yenilenir.
    socket.emit("cupGrinderSensor:watch", { serialNo }, (ack) => {
      if (ack?.ok) {
        setStatus("watching");
        setError(null);
        // Yeniden bağlanmada uzaktan oturum da yenilensin
        if (runningRef.current) {
          socket.emit("cupGrinderSensor:start", { serialNo }, () => {});
        }
      } else {
        setStatus("error");
        setError(ack?.reason || "error");
      }
    });
    return () => {
      try {
        socket.emit("cupGrinderSensor:unwatch", { serialNo });
      } catch (_) {}
    };
  }, [socket, ready, serialNo]);

  // Bayatlik kontrolu icin saniyede bir yeniden ciz (yalniz veri varken).
  useEffect(() => {
    if (!receivedAt) return undefined;
    const t = setInterval(() => forceTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [receivedAt]);

  useEffect(() => {
    setRunning(false);
    setRemoteError(null);
    setHistory([]);
    setLive(null);
    setReceivedAt(null);
  }, [serialNo]);

  const start = useCallback(() => {
    if (!socket || !ready || !serialNo) return;
    setRemoteError(null);
    socket.emit("cupGrinderSensor:start", { serialNo }, (ack) => {
      if (ack?.ok) setRunning(true);
      else setRemoteError(ack?.reason || "error");
    });
  }, [socket, ready, serialNo]);

  const stop = useCallback(() => {
    setRunning(false);
    if (socket && serialNo)
      socket.emit("cupGrinderSensor:stop", { serialNo }, () => {});
  }, [socket, serialNo]);

  const isFresh = receivedAt != null && Date.now() - receivedAt < STALE_MS;

  return {
    status,
    error,
    live: isFresh ? live : null,
    receivedAt,
    closedReason,
    running,
    remoteError,
    history,
    start,
    stop,
  };
}
